/**
 * ThreeDayView - Three column layout showing Today + 2 days.
 *
 * Shows a 3-day planning horizon with compact cards stacked vertically
 * in each column. Waiting tasks (assigned to others) are shown separately.
 */

import React, { useMemo } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import { Participant, PlanRole } from '../../../types/generated/graphql';
import {
  ScheduleResult,
  ScheduledStep,
  DaySchedule,
  getTasksForDate,
} from '../../../utils/calendarScheduler';
import { colors } from '../../../theme/colors';
import TaskCard from './TaskCard';

interface ThreeDayViewProps {
  scheduleResult: ScheduleResult;
  currentDate: Date;
  onDateChange: (date: Date) => void;
  participants: Participant[];
  roles?: PlanRole[];
  currentParticipantId: string | null;
  onNavigateToTask: (taskId: string) => void;
}

/**
 * Add days to a date.
 */
function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

/**
 * Format date for column header (e.g., "Sun, Dec 22").
 */
function formatColumnHeader(date: Date): string {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dateNorm = new Date(date);
  dateNorm.setHours(0, 0, 0, 0);

  if (dateNorm.getTime() === today.getTime()) {
    return `Today, ${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
  }

  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Format date range for header (e.g., "Dec 22 - Dec 24").
 */
function formatDateRange(startDate: Date, endDate: Date): string {
  const startStr = startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const endStr = endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return `${startStr} - ${endStr}`;
}

/**
 * Get assignee from participants.
 */
function getAssignee(
  step: ScheduledStep,
  participants: Participant[]
): Participant | null {
  if (!step.step.assignedToId) return null;
  return participants.find((p) => p.id === step.step.assignedToId) || null;
}

/**
 * Check if a date is today.
 */
function isToday(date: Date): boolean {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dateNorm = new Date(date);
  dateNorm.setHours(0, 0, 0, 0);
  return dateNorm.getTime() === today.getTime();
}

/**
 * Day column component.
 */
interface DayColumnProps {
  date: Date;
  daySchedule: DaySchedule | null;
  participants: Participant[];
  onNavigateToTask: (taskId: string) => void;
}

const DayColumn: React.FC<DayColumnProps> = ({
  date,
  daySchedule,
  participants,
  onNavigateToTask,
}) => {
  // Collect all MY tasks for this day (excludes waiting tasks)
  const myTasks: ScheduledStep[] = useMemo(() => {
    if (!daySchedule) return [];
    return [
      ...daySchedule.allDayTasks,
      ...daySchedule.morningTasks,
      ...daySchedule.afternoonTasks,
      ...daySchedule.eveningTasks,
    ];
  }, [daySchedule]);

  // Waiting tasks are separate
  const waitingTasks = daySchedule?.waitingTasks || [];
  const totalTasks = myTasks.length + waitingTasks.length;
  const isTodayColumn = isToday(date);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        overflow: 'hidden',
      }}
    >
      {/* Column header */}
      <Box
        sx={{
          py: 1.5,
          px: 1,
          textAlign: 'center',
          borderBottom: `2px solid ${isTodayColumn ? colors.blue[500] : colors.gray[200]}`,
          bgcolor: isTodayColumn ? colors.blue[50] : 'transparent',
          borderRadius: '4px 4px 0 0',
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 600,
            fontSize: '0.75rem',
            color: isTodayColumn ? colors.blue[700] : colors.textPrimary,
          }}
        >
          {formatColumnHeader(date)}
        </Typography>
        {totalTasks > 0 && (
          <Typography
            variant="caption"
            sx={{
              color: isTodayColumn ? colors.blue[500] : colors.gray[400],
              fontSize: '0.6rem',
            }}
          >
            {myTasks.length} task{myTasks.length !== 1 ? 's' : ''}
            {waitingTasks.length > 0 && ` + ${waitingTasks.length} waiting`}
          </Typography>
        )}
      </Box>

      {/* Task cards */}
      <Box
        sx={{
          p: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.75,
          minHeight: 200,
          overflow: 'auto',
        }}
      >
        {/* My tasks */}
        {myTasks.map((scheduledStep) => (
          <TaskCard
            key={scheduledStep.step.id}
            scheduledStep={scheduledStep}
            variant="compact"
            assignee={getAssignee(scheduledStep, participants)}
            isWaiting={scheduledStep.isWaiting}
            onClick={() => onNavigateToTask(scheduledStep.step.id)}
          />
        ))}

        {/* Waiting tasks - shown after my tasks with visual separator */}
        {waitingTasks.length > 0 && (
          <>
            {myTasks.length > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  mt: 1,
                  mb: 0.5,
                }}
              >
                <HourglassEmptyIcon sx={{ fontSize: 10, color: colors.amber[500] }} />
                <Typography
                  variant="caption"
                  sx={{ fontSize: '0.55rem', color: colors.amber[600], fontWeight: 600 }}
                >
                  Waiting
                </Typography>
              </Box>
            )}
            <Box sx={{ opacity: 0.7 }}>
              {waitingTasks.map((scheduledStep) => (
                <Box key={scheduledStep.step.id} sx={{ mb: 0.75 }}>
                  <TaskCard
                    scheduledStep={scheduledStep}
                    variant="compact"
                    assignee={getAssignee(scheduledStep, participants)}
                    isWaiting={true}
                    onClick={() => onNavigateToTask(scheduledStep.step.id)}
                  />
                </Box>
              ))}
            </Box>
          </>
        )}

        {/* Empty state - only if no tasks at all */}
        {totalTasks === 0 && (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: `1px dashed ${colors.gray[200]}`,
              borderRadius: 1.5,
              py: 3,
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: colors.gray[400], fontSize: '0.65rem' }}
            >
              No tasks
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

/**
 * Main ThreeDayView component.
 */
export const ThreeDayView: React.FC<ThreeDayViewProps> = ({
  scheduleResult,
  currentDate,
  onDateChange,
  participants,
  currentParticipantId,
  onNavigateToTask,
}) => {
  // Calculate the 3 days to show
  const days = useMemo(() => {
    return [
      currentDate,
      addDays(currentDate, 1),
      addDays(currentDate, 2),
    ];
  }, [currentDate]);

  // Get schedules for each day
  const daySchedules = useMemo(() => {
    return days.map((date) => getTasksForDate(scheduleResult, date));
  }, [days, scheduleResult]);

  // Navigate by 3 days
  const handlePrevDays = () => {
    onDateChange(addDays(currentDate, -3));
  };

  const handleNextDays = () => {
    onDateChange(addDays(currentDate, 3));
  };

  const endDate = addDays(currentDate, 2);

  return (
    <Box>
      {/* Header with date navigation */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          mb: 2,
          py: 1,
        }}
      >
        <IconButton
          onClick={handlePrevDays}
          size="small"
          sx={{ color: colors.gray[500] }}
        >
          <ChevronLeftIcon />
        </IconButton>

        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: colors.textPrimary,
            fontSize: '1rem',
            minWidth: 180,
            textAlign: 'center',
          }}
        >
          {formatDateRange(currentDate, endDate)}
        </Typography>

        <IconButton
          onClick={handleNextDays}
          size="small"
          sx={{ color: colors.gray[500] }}
        >
          <ChevronRightIcon />
        </IconButton>
      </Box>

      {/* Three columns */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          border: `1px solid ${colors.gray[200]}`,
          borderRadius: 2,
          bgcolor: colors.card,
          overflow: 'hidden',
        }}
      >
        {days.map((date, index) => (
          <Box
            key={date.toISOString()}
            sx={{
              borderRight: index < 2 ? `1px solid ${colors.gray[200]}` : 'none',
            }}
          >
            <DayColumn
              date={date}
              daySchedule={daySchedules[index]}
              participants={participants}
              onNavigateToTask={onNavigateToTask}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default ThreeDayView;
